import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  PauseIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const AdminShipments = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    status: '',
    serviceType: '',
    startDate: '',
    endDate: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const queryClient = useQueryClient();

  const { data: shipmentsData, isLoading, refetch } = useQuery({
    queryKey: ['admin-shipments', filters],
    queryFn: () => adminAPI.getShipments(filters),
    placeholderData: keepPreviousData,
  });

  const holdMutation = useMutation({
    mutationFn: ({ id, reason, holdType, facilityName }) => adminAPI.holdShipment(id, reason, holdType, facilityName),
    onSuccess: () => {
      toast.success('Shipment placed on hold');
      queryClient.invalidateQueries(['admin-shipments']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to hold shipment');
    }
  });

  const releaseMutation = useMutation({
    mutationFn: (id) => adminAPI.releaseShipment(id),
    onSuccess: () => {
      toast.success('Shipment released from hold');
      queryClient.invalidateQueries(['admin-shipments']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to release shipment');
    }
  });

  const handleHoldShipment = (id) => {
    const holdType = prompt('Select hold type:\n1. Customs Inspection\n2. Import Duties\n3. Missing Documentation\n4. Restricted Items\n5. Security Check\n\nEnter number (1-5):');
    if (holdType === null) return;

    const typeMap = {
      '1': 'customs_inspection',
      '2': 'import_duties',
      '3': 'missing_documentation',
      '4': 'restricted_items',
      '5': 'security_check'
    };

    const selectedType = typeMap[holdType];
    if (!selectedType) {
      toast.error('Invalid hold type selected');
      return;
    }

    const facilityName = prompt('Enter customs facility name (e.g., Istanbul Customs, JFK Customs):');
    if (facilityName === null) return;

    const reason = prompt('Enter additional details (optional):');
    if (reason === null) return;

    holdMutation.mutate({ id, reason: reason || '', holdType: selectedType, facilityName: facilityName || 'Customs Facility' });
  };

  const handleReleaseShipment = (id) => {
    releaseMutation.mutate(id);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      status: '',
      serviceType: '',
      startDate: '',
      endDate: ''
    });
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'exception', label: 'Exception' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const serviceTypeOptions = [
    { value: '', label: 'All Services' },
    { value: 'standard', label: 'Standard' },
    { value: 'express', label: 'Express' },
    { value: 'overnight', label: 'Overnight' },
    { value: 'economy', label: 'Economy' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0B1F3A]">Shipments</h1>
            <p className="text-[#64748B] mt-2">
              Manage and track all shipments in the system
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              to="/admin/shipments/create"
              className="btn btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Shipment</span>
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="card mb-6">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by tracking number, recipient, or sender..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="btn btn-outline flex items-center space-x-2"
              >
                <FunnelIcon className="h-5 w-5" />
                <span>Filters</span>
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Search
              </button>
            </div>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="input"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type
                  </label>
                  <select
                    value={filters.serviceType}
                    onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                    className="input"
                  >
                    {serviceTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="input"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="btn btn-secondary"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Shipments Table */}
        <div className="card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tracking Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sender
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {shipmentsData?.data?.shipments?.map((shipment) => (
                      <tr key={shipment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-[#0B1F3A]">
                            {shipment.tracking_number}
                          </div>
                          {shipment.package_description && (
                            <div className="text-sm text-[#64748B]">
                              {shipment.package_description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-[#0B1F3A]">{shipment.sender_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-[#0B1F3A]">{shipment.recipient_name}</div>
                          <div className="text-sm text-[#64748B]">
                            {shipment.recipient_city}, {shipment.recipient_country}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`status-badge status-${shipment.current_status}`}>
                            {shipment.current_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          {shipment.current_location && (
                            <div className="text-xs text-gray-500 mt-1">
                              {shipment.current_location}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-[#0B1F3A] capitalize">
                            {shipment.service_type}
                          </span>
                          {shipment.package_weight && (
                            <div className="text-xs text-gray-500">
                              {shipment.package_weight} kg
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(shipment.created_at), 'MMM dd, yyyy')}
                          <div className="text-xs">
                            {format(new Date(shipment.created_at), 'h:mm a')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                              to={`/admin/shipments/${shipment.id}`}
                              className="text-[#FF6B00] hover:text-[#e65f00] flex items-center space-x-1"
                            >
                              <EyeIcon className="h-4 w-4" />
                              <span>View</span>
                            </Link>
                            {shipment.current_status === 'on_hold' ? (
                              <button
                                onClick={() => handleReleaseShipment(shipment.id)}
                                disabled={releaseMutation.isLoading}
                                className="text-[#16A34A] hover:text-[#0f7d36] flex items-center space-x-1 disabled:opacity-50"
                                title="Release from hold"
                              >
                                <PlayIcon className="h-4 w-4" />
                                <span>Release</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleHoldShipment(shipment.id)}
                                disabled={holdMutation.isLoading}
                                className="text-orange-600 hover:text-orange-900 flex items-center space-x-1 disabled:opacity-50"
                                title="Place on hold"
                              >
                                <PauseIcon className="h-4 w-4" />
                                <span>Hold</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          No shipments found. <Link to="/admin/shipments/create" className="text-[#FF6B00] hover:text-[#e65f00]">Create your first shipment</Link>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {shipmentsData?.data?.pagination && shipmentsData.data.pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handleFilterChange('page', filters.page - 1)}
                      disabled={filters.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handleFilterChange('page', filters.page + 1)}
                      disabled={filters.page >= shipmentsData.data.pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(filters.page - 1) * filters.limit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(filters.page * filters.limit, shipmentsData.data.pagination.totalItems)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">{shipmentsData.data.pagination.totalItems}</span>{' '}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handleFilterChange('page', filters.page - 1)}
                          disabled={filters.page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(Math.min(5, shipmentsData.data.pagination.totalPages))].map((_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => handleFilterChange('page', page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === filters.page
                                  ? 'z-10 bg-orange-50 border-[#FF6B00] text-[#FF6B00]'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handleFilterChange('page', filters.page + 1)}
                          disabled={filters.page >= shipmentsData.data.pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminShipments;
